/**
 * Time formatting utilities for consistent local timezone display
 * 
 * CRITICAL RULE: All times are always displayed in the user's local timezone.
 * 
 * Storage: Times are stored as ISO 8601 UTC strings in the database.
 * Display: Times are always shown in the user's local timezone.
 * Creation: When creating Date objects from user input, interpret them as local time.
 * Parsing: JavaScript's new Date() automatically converts UTC ISO strings to local time.
 */

/**
 * Format time in local timezone
 * @param date - Date object (will be displayed in local timezone)
 * @param options - Optional Intl.DateTimeFormatOptions for customization
 * @returns Formatted time string in local timezone
 */
export function formatTime(
  date: Date,
  options?: Intl.DateTimeFormatOptions
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  return date.toLocaleTimeString('en-US', { ...defaultOptions, ...options });
}

/**
 * Format date in local timezone
 * @param date - Date object (will be displayed in local timezone)
 * @param format - Format style: 'short', 'long', or 'month-year'
 * @returns Formatted date string in local timezone
 */
export function formatDate(
  date: Date,
  format: 'short' | 'long' | 'month-year' = 'short'
): string {
  let options: Intl.DateTimeFormatOptions;

  if (format === 'short') {
    options = { month: 'short', day: 'numeric', year: 'numeric' };
  } else if (format === 'long') {
    options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
  } else {
    options = { month: 'long', year: 'numeric' };
  }

  return date.toLocaleDateString('en-US', options);
}

/**
 * Format date and time in local timezone
 * @param date - Date object (will be displayed in local timezone)
 * @param options - Optional configuration
 * @returns Formatted date and time string in local timezone
 */
export function formatDateTime(
  date: Date,
  options?: {
    dateFormat?: 'short' | 'long' | 'month-year';
    timeFormat?: Intl.DateTimeFormatOptions;
    separator?: string;
  }
): string {
  const dateStr = formatDate(date, options?.dateFormat || 'short');
  const timeStr = formatTime(date, options?.timeFormat);
  const separator = options?.separator || ' at ';

  return `${dateStr}${separator}${timeStr}`;
}

/**
 * Parse date and time strings as local time (not UTC)
 * This is used when creating events from user input (form fields)
 * @param dateString - Date string in YYYY-MM-DD format
 * @param timeString - Optional time string in HH:mm format
 * @returns Date object representing the local time
 */
export function parseLocalDate(
  dateString: string,
  timeString?: string
): Date {
  if (timeString) {
    // Parse as local time: "2024-01-15T14:00" is interpreted as local time
    return new Date(`${dateString}T${timeString}`);
  } else {
    // Parse date only (midnight local time)
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
}

/**
 * Create Date object in local timezone
 * @param year - Year
 * @param month - Month (1-12, not 0-11)
 * @param day - Day of month
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @returns Date object representing the local time
 */
export function createLocalDateTime(
  year: number,
  month: number,
  day: number,
  hour: number = 0,
  minute: number = 0
): Date {
  // JavaScript Date constructor uses local timezone when given individual components
  return new Date(year, month - 1, day, hour, minute);
}

/**
 * Format time range (start - end) in local timezone
 * @param startDate - Start date/time
 * @param endDate - End date/time
 * @param options - Optional configuration
 * @returns Formatted time range string
 */
export function formatTimeRange(
  startDate: Date,
  endDate: Date,
  options?: {
    showDate?: boolean;
    sameDayFormat?: 'short' | 'long';
  }
): string {
  const showDate = options?.showDate ?? false;
  const sameDayFormat = options?.sameDayFormat || 'short';

  const isSameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  if (isSameDay) {
    const dateStr = showDate ? `${formatDate(startDate, sameDayFormat)} ` : '';
    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);
    return `${dateStr}${startTime} - ${endTime}`;
  } else {
    const startStr = formatDateTime(startDate, { dateFormat: sameDayFormat });
    const endStr = formatDateTime(endDate, { dateFormat: sameDayFormat });
    return `${startStr} - ${endStr}`;
  }
}

/**
 * Format a Date object as YYYY-MM-DD string in local timezone
 * This is important because toISOString() converts to UTC, which can shift the date by a day
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format (local timezone)
 */
export function formatDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
